import ReactGA from 'react-ga'

import User from './models/User'
import Client from './models/Client'
import Project from './models/Project'
import Team from './models/Team'
import Opportunity from "./models/Opportunity";
import ProjectGroup from "./models/ProjectGroup";
import TalentCandidate from "./models/TalentCandidate";
import {FinancialsQueryKeys} from "./config/QueryKeys";
import {clearTalentStatusSeen, hasSeenTalentStatus} from "./views/talent-application/components/applicationProgress";

class Auth extends TB.Auth
{
	static Model = User;

	/**
	 * Reset the once-per-login talent status screen on every login/logout.
	 */
	static async login(creds)
	{
		clearTalentStatusSeen();
		return super.login(creds);
	}

	static async logout()
	{
		clearTalentStatusSeen();
		return super.logout();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static clearUser()
	{
		this.TwoFactorConfirmed = false;

		ReactGA.set({ userId: undefined, userType: undefined });

		this.clearClientScopedQueries();
        Project.clearCache();
		TB.clearLocalStorage();

		if (window.FS) {
			window.FS.identify(undefined);
		}
		if (TB.BugsnagClient) {
            TB.BugsnagClient.user = {
                id: null,
                type: null,
                name: null
            }
        }
		if (window.Intercom) {
			window.Intercom('update', {
			    user_id: null,
			    name: null,
			    email: null,
			    user_type: null,
				registered_at: null,
				status: null,
			});
		}

		Project.clearCache();
		Team.clearCache();
		TalentCandidate.clearCache();
		Opportunity.clearCache();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static refreshUser()
	{
	    let user = this.user();

        // console.count("refreshUser")
		if (!user) {
			return this.clearUser();
		}

		ReactGA.set({ userId: user.id, userType: user.type });

		if (TB.BugsnagClient) {
		    TB.BugsnagClient.user = {
                id: user.id,
                type: user.type,
                name: user.firstNamePlusInitial?.()
            }
        }
		if (window.Intercom) {
			window.Intercom('update', {
			    user_id: user.id,
			    name: `${user.first_name} ${user.last_name}`,
			    email: user.email,
			    user_type: user.type,
				registered_at: user.registered_at,
				status: user.status,
			});
		}
		if (window.FS)
		{
			let trackingId = user.id
			let trackingProps = {
			  	displayName: `${user.first_name} ${user.last_name}`,
			  	email: user.email,
			  	// Add your own custom user variables here, details at
			  	// https://help.fullstory.com/hc/en-us/articles/360020623294-FS-setUserVars-API-Capturing-custom-user-properties
			  	accountType: user.type,
			  	accountStatus: user.status,
			};
			if (user.impersonated_by) {
				trackingId = user.impersonated_by.id
				trackingProps.displayName = `${user.impersonated_by.first_name} ${user.impersonated_by.last_name}`;
				trackingProps.email = user.impersonated_by.email
				trackingProps.accountType = user.impersonated_by.type

				trackingProps.impersonatedId = user.id
				trackingProps.impersonatedName = `${user.first_name} ${user.last_name}`
				// trackingProps.impersonatedEmail = user.email
				// trackingProps.impersonatedAccountType = user.type
			}

			if (user.isClient() && user.client_organization && !user.impersonated_by) {
				trackingProps.clientId = user.client_organization.id;
				trackingProps.clientName = user.client_organization.name;
				switch (true) {
					case user.is_client_super_admin:
						trackingProps.clientRole = 'Super Admin'; break;
					case user.is_client_admin:
						trackingProps.clientRole = 'Admin'; break;
					default:
						trackingProps.clientRole = 'Base User'; break;
				}
				trackingProps.clientIsSupplier = user.client_organization.is_supplier;
			}

			window.FS.identify(trackingId, trackingProps);
		}

        let hasSeenTalentWelcome = TB.isDefined(
        	localStorage.getItem('Has_Seen_Talent_Welcome')
    	);
    	if (hasSeenTalentWelcome && user.was_talent_approved_since_last_session) {
            this.User.was_talent_approved_since_last_session = false;
    		this.update(user, true);
    	}
	}

	static shouldSeeTalentWelcome()
	{
    	if (!laravel.is_core_platform) {
    		return false;
    	}

	    let user = this.user();
		if (!user
			|| !user.isTalent()
				|| !user.was_talent_approved_since_last_session || this.shouldSeeTalentAppUpdate()) {
			return false;
		}

		return true;
	}

    static shouldSeeTalentAppNotApproved()
    {
        return this.user()?.isTalent() && this.user()?.isTalent().rejected_at
    }
    static shouldSeeTalentAppUpdate()
    {
        if (!laravel.is_core_platform) {
            return false;
        }

        let user = this.user();

        if (!user?.should_see_version_updates?.['3_5_0']) {
            return false;
        }

        return true;
    }

    static async markTalentAppUpdateAsViewed()
    {
        localStorage.setItem('Has_Seen_Talent_Welcome', 'true');

        let user = this.user()
        user.should_see_version_updates = {
            ['3_5_0']: false
        };
        return this.update(user);
    }

	static async persistConferenceLinkToProfile(preferred_conference_link)
	{
		if(!preferred_conference_link) return
		let user = this.user()
		user.preferred_meeting_link = preferred_conference_link;

		return this.update(user);
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onExpired()
	{
		await this.clearUser();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onRefresh()
	{
		await this.refreshUser();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onUpdate()
	{
		await this.refreshUser();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async markTalentWelcomeAsViewed()
	{
        localStorage.setItem('Has_Seen_Talent_Welcome', 'true');

		let user = this.user()
        user.was_talent_approved_since_last_session = false;
        this.update(user);
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onLogin()
	{
		await super.onLogin();

		if (this.TwoFactorEnabled && !this.TwoFactorConfirmed) {
			return;
		}

		TB.clearLocalStorage();

		let user = this.user();
		if (TB.isDefined(user.deactivated_at))
		{
			let shouldReactivate = await TB.sysAlert(
	            'talent.reactivate_account'
	        );

	        if (!shouldReactivate) {
	        	return this.logout();
	        }

			let didReactivate = await this.reactivateAccount();
			if (!didReactivate) {
				return;
			}
		}

		await this.refreshUser();
	}

	/**
     * [Note]
     *
     * @returns {Promise}
     */
	static async onLogout()
	{
		await super.onLogout();

		localStorage.removeItem('Has_Seen_Talent_Welcome');

		await this.clearUser();
	}

	static registeredClientUser()
	{
		let user = Auth.user();
		if (!user) {
			return false;
		}

		if (!user.canManageClients() && !user.hasRegisteredClientOrg()) {
			return false;
		}

		return user;
	}

	static registeredSupplierClientResourceManager()
	{
		let user = Auth.user();
		if (!user) {
			return false;
		}

		if (!user.canManageClientResourceTalent()) {
			return false;
		}

		return user;
	}

    static registeredTalentUser()
    {
        let user = Auth.user();
        if (!user || (!user.isTalent() && !user.canManageTalent())) {
            return false;
        }

        if (user.isTalent() && !user.isProfileApproved()) {
            return false;
        }

        return user;
    }

	/**
     * [Note]
     *
     * @returns {String}
     */
	static appropriate()
	{

		let redirect = super.appropriate();

        if (redirect) {
			return redirect;
		}

		if (this.user().isClientManager()) {
			return '/client/manager/projects';
        }

        if ((this.user().isClientConsultant() || this.user().isClient())
        	&& !this.user().hasClientOrg()) {
            return '/client/org/select-organization';
        }

        if (this.user().isClient()
        	|| this.user().isClientConsultant())
        {
			if (!this.user().hasRegisteredClientOrg()) {
				return '/client/org/registration';
			}
			// else if (!this.user().clientOrg().hasProjects()) {
			// 	return '/clients/first-project';
			// }
			else {
				return '/client/dashboard'
			}
		}

		if (this.user().isTalent() || this.user().isTalentSupplierResource()) {
			// if (this.user().mustSignAgreement()) {
			// 	return '/talent/contracts';
			// }

			if (this.user().isApplicationRejected()) {
				return '/talent/review-completed';
			}
            if (this.user().isBeingReviewed()) {
				return '/talent/under-review';
			}
			if (!this.user().isApplicationApproved() && !this.user().isTalentSupplierResource()) {
				// First visit: the application overview. Returning applicants
				// (Basic Information saved) get the "pick up where you left off" screen.
				return this.user().validateInfo()
					? '/talent/welcome-back'
					: '/talent/application/get-started';
			}
            if (this.shouldSeeTalentWelcome()) {
                return '/talent/welcome';
            }
            // Until the profile is approved, show where they are once per login
            if (!this.user().isProfileApproved()
                && !this.user().isTalentSupplierResource()
                && !hasSeenTalentStatus(this.user())) {
                return '/talent/welcome-back';
            }
            // if (this.shouldSeeTalentAppUpdate()) {
            //     return '/talent/welcome-update';
            // }

            if (!this.user().hasCompletedApplication()) {
                return this.user().currentApplicationStep().getPath();
            }

			return '/talent/dashboard';
		}

        if (this.user().isClientManager()) {
            return '/client/manager/projects';
        }
        if (this.user().isTalentManager()) {
			return '/talent/manager/talent-vetting';
		}
        if (this.user().isPlatformUser()) {
            return '/dashboard';
        }

		return '/login';
	}

	static shouldHideNavbar(location)
	{
        if (location?.pathname?.startsWith('/settings/notifications/')) {
        	return false;
        }

		let user = this.user();

		if (this.DidExpire) {
		    return true
        }

        if (!user) {
        	return true;
        }

		return  !this.loaded()
			// || (user && user.isPlatformUser())
			|| location?.pathname === '/talent/review-completed'
			|| location?.pathname === '/talent/under-review'
			|| location?.pathname === '/talent/welcome-update'
			|| location?.pathname?.includes('/talent/welcome')
			|| location?.pathname === '/signup/talent'
			|| location?.pathname === '/login/preview';
	}

	/**
     * [Note]
     *
     * @param {Object} location
     * @returns {Boolean}
     */
	static isAppropriate(location)
	{
		let didLoad = this.loaded(),
			exists = didLoad && this.exists(),
			user = this.user(),
			isPlatformUser = exists && user.isPlatformUser(),
			isClient = exists && user.isClient(),
			isClientConsultant = exists && user.isClientConsultant(),
			isClientManager = exists && user.isClientManager(),
			isTalent = exists && user.isTalent(),
			isActiveTalent = exists && user.isTalent() && user.isActive(),
			canManageTalent = exists && user.canManageTalent(),
			canManageClients = exists && user.canManageClients(),
			isRegisteredClientOrg = (isClient || isPlatformUser) && user.hasRegisteredClientOrg();

		switch (location.pathname)
		{
		    /*
		    |--------------------------------------------------------------------------
		    | Public Routes
		    |--------------------------------------------------------------------------
		    */

			case '/legal/terms':
			case '/legal/privacy-policy':
			case '/legal/cookie-policy':
				return true;

		    /*
		    |--------------------------------------------------------------------------
		    | Auth/Session Routes
		    |--------------------------------------------------------------------------
		    */

            case '/':
                return !didLoad;

            case '/settings/notifications/manage':
            case '/settings/notifications/all/opt-out':
            case (location.pathname.match(/^\/settings\/notifications\/email\/(.*)\/opt-out/) || {}).input:
                return didLoad;

			case '/password/forgot':
            case (location.pathname.match(/^\/password\/reset\/(.*)/) || {}).input:
				return didLoad && !exists;

			case '/signup/talent':
			case '/login':
			case '/register':
            case (location.pathname.match(/^\/register\/(.*)/) || {}).input:
			case (location.pathname.match(/^\/signup\/talent\/(.*)/) || {}).input:
			case (location.pathname.match(/^\/signup\/invite\/talent\/(.*)/) || {}).input:
				return didLoad
					&& !exists
					&& !this.NeedsTwoFactorConfirmed;

			case '/authorize':
				return didLoad
					&& !exists
					&& this.isValidated() === false
					&& this.NeedsTwoFactorConfirmed;

			case '/account/deactivate':
                return didLoad && exists


			/*
		    |--------------------------------------------------------------------------
		    | Talent Routes
		    |--------------------------------------------------------------------------
		    */
			case '/talent/application/review':
				return didLoad
					&& isTalent
					// && !user.mustSignAgreement()
					&& !user.isApplicationApproved()
					&& !user.isApplicationRejected()
					&& !user.isBeingReviewed();

			case '/talent/application':
			case '/talent/application/':
				return false
			case (location.pathname.match(/^\/talent\/application/) || {}).input:
				return didLoad
					&& isTalent
                    && !user.isApplicationRejected()
					// && !user.mustSignAgreement()
					&& !user.isBeingReviewed();

            case '/talent/application-saved':
                return didLoad
                    && isTalent
                    && !user.isApplicationRejected()
                    && !user.isBeingReviewed();

            case '/talent/under-review':
                return didLoad
                    && isTalent
                    && !user.isApplicationRejected()
                    && user.isBeingReviewed();
            case '/talent/interview':
                return didLoad
                    && isTalent;
            case '/talent/review-completed':
                return didLoad
                    && isTalent
                    && user.isApplicationRejected();
            case '/talent/welcome-back':
                return didLoad
                    && isTalent
                    && !user.isProfileApproved();
            case '/talent/welcome':
                return didLoad
                    && isTalent
                    && !user.isApplicationRejected()
                    && this.shouldSeeTalentWelcome();
            // case '/talent/welcome-update':
            //     return didLoad
            //         && isTalent
            //         && !user.isApplicationRejected()
            //         && this.shouldSeeTalentAppUpdate();

            case (location.pathname.match(/^\/talent\/manager\/(.*)/) || {}).input:
                return didLoad
                    && canManageTalent;

            case (location.pathname.match(/^\/talent\/(.*)/) || {}).input:
                return didLoad
                    && (isActiveTalent || canManageTalent || user?.canManageClientResourceTalent());

            /*
            |--------------------------------------------------------------------------
            | Client OR Talent Routes
            |--------------------------------------------------------------------------
            */
            case (location.pathname.match(/^\/talent-profiles\/(.*)/) || {}).input:
                return didLoad
                    && (isRegisteredClientOrg || isActiveTalent || canManageTalent);
		    /*
		    |--------------------------------------------------------------------------
		    | Client Routes
		    |--------------------------------------------------------------------------
		    */
            case '/client/org/select-organization':
                return didLoad
                    && (isClientConsultant || isClient)
                    && !user?.hasClientOrg();

            case '/client/org/registration':
                return didLoad
                    && exists
                    && !isRegisteredClientOrg;
            case '/client/profile':
                return didLoad
                    && exists
                    && isRegisteredClientOrg;

			case '/client/my-groups':
				return didLoad
					&& exists
					&& isRegisteredClientOrg;

            case '/users/invite':
			case (location.pathname.match(/^\/users\/(.*)\/edit/) || {}).input:
			case (location.pathname.match(/^\/client\/users\/(.*)/) || {}).input:
                return didLoad
                    && exists
                    && isRegisteredClientOrg
                    && (user?.isClientConsultant() || user?.isClientAdmin());

            case '/client/manager/projects':
                return didLoad
                    && exists
                    && canManageClients;

			case (location.pathname.match(/^\/projects\/(.*)/) || {}).input:
			case (location.pathname.match(/^\/client\/(.*)/) || {}).input:
				return didLoad
					&& exists
                    && isRegisteredClientOrg;







			// case '/clients/first-project':
			// 	return didLoad
			// 		&& isRegisteredClientOrg
			// 		&& !user.clientOrg().hasProjects();



		    /*
		    |--------------------------------------------------------------------------
		    | General Routes
		    |--------------------------------------------------------------------------
		    */

			case '/system':
				return didLoad && (isPlatformUser || isClientConsultant);

			case '/dashboard':
				return didLoad && isPlatformUser;

			case '/profile':
			case '/settings':
			case '/password/update':
				return didLoad
					&& exists
					&& (!isClient || isRegisteredClientOrg);

			default:
				return didLoad;
		}
	}

    /**
     * [Note]
     *
     * @param {Client} client
     * @returns {Promise}
     */
    static async startClientSession(client)
    {
    	if (!this.user().isClient()
    		&& !this.user().isClientConsultant()
    		&& !this.user().canManageClients()) {
    		throw new Error("Unauthorized");
    	}
        if (!client?.id) {
            throw new Error("Unknown client");
        }

        let endpoint = TB.api_endpoint(`/api/clients/${client.id}/session`);

        this.ClientSessionRequest = TB.request(
            endpoint
        );

        let response = await this.ClientSessionRequest.post().catch(
            console.warn
        );

        if (!response) {
            this.ClientSessionRequest.didHandleError = await this.ClientSessionRequest.handleError();
            if (this.ClientSessionRequest.didHandleError
                && this.ClientSessionRequest.shouldRerun) {
                return this.startClientSession(client);
            }

            return false;
        }

        this.User = { ...response.success.data.auth };
        this.DidExpire = false;

        this.clearClientScopedQueries();
        await Project.clearCache();
        await this.onUpdate();

        TB.event('Auth@update');

        return this.user();
    }

    /**
     * Drop cached query data that belongs to the previous client session.
     *
     * These are removed rather than invalidated: invalidating keeps the old
     * payload in the cache, which would paint the previous client's figures on
     * screen while the refetch is still in flight.
     *
     * @returns {void}
     */
    static clearClientScopedQueries()
    {
        if (!TB.queryClient) {
            return;
        }

        FinancialsQueryKeys.forEach((key) => {
            TB.queryClient.removeQueries({queryKey: [key]});
        });
    }


	/**
     * [Note]
     *
     * @param {Object} creds
     * @returns {Promise}
     */
	static async talentSignup(creds)
	{
		let endpoint = TB.api_endpoint('/api/sessions/signup/talent');
		if (creds.token) {
			endpoint = TB.api_endpoint('/api/sessions/register');
		}

		this.TalentSignupRequest = TB.request(
			endpoint, { ...creds }
		);

		let response = await this.TalentSignupRequest.post().catch(
			console.warn
		);

		// let needs2FA = response && this.checkResponseRequires2FA(response)
  //       if (needs2FA) {
  //           return;
  //       }

		if (!response) {
            this.TalentSignupRequest.didHandleError = await this.TalentSignupRequest.handleError();
            if (this.TalentSignupRequest.didHandleError
                && this.TalentSignupRequest.shouldRerun) {
                return this.talentSignup(creds);
            }

			return false;
		}

		this.User = { ...response.success.data.auth };
		this.DidExpire = false;

		await this.onLogin();

		TB.event('Auth@login');

		return this.user();
	}

    /**
     * [Note]
     *
     * @returns {Promise}
     */
    static talentHasInputResponseError() {
        return TB.isObject(this.TalentUpdateRequest)
            && TB.isObject(this.TalentUpdateRequest.response)
            && TB.isObject(this.TalentUpdateRequest.response.data)
            && TB.isObject(this.TalentUpdateRequest.response.data.error)
            && this.TalentUpdateRequest.response.data.error.message === 'invalid_input_format';
    }
	/**
     * [Note]
     *
     * @returns {Promise}
     */
    static supplierHasInputResponseError() {
        return TB.isObject(this.SupplierUpdateRequest)
            && TB.isObject(this.SupplierUpdateRequest.response)
            && TB.isObject(this.SupplierUpdateRequest.response.data)
            && TB.isObject(this.SupplierUpdateRequest.response.data.error)
            && this.SupplierUpdateRequest.response.data.error.message === 'invalid_input_format';
    }

	/**
     * [Note]
     *
     * @param {Object} params
     * @returns {Promise}
     */
	static async talentUpdate(params)
	{
		this.TalentUpdateRequest = TB.request(
			TB.api_endpoint('/api/sessions/update'), { ...params }
		);

        this.TalentUpdateRequest = this.TalentUpdateRequest.headers({
            'Content-Type': 'multipart/form-data'
        });

		let response = await this.TalentUpdateRequest.post().catch(e => { console.warn('ERROR: talentUpdate', e); return false })

		if (!response) {
            this.TalentUpdateRequest.didHandleError = await this.TalentUpdateRequest.handleError();
            if (this.TalentUpdateRequest.didHandleError
                && this.TalentUpdateRequest.shouldRerun) {
                return this.talentUpdate(params);
            }

			return false;
		}

		return this.update(User.make({ ...response.success.data.auth }))
	}

	/**
     * [Note]
     *
     * @param {Object} params
     * @returns {Promise}
     */
	static async submitAppealForRejectedProfile(params) {
		this.TalentAppealRequest = TB.request(
			TB.api_endpoint('/api/appeals/talent-user-profile/create'), { ...params }
		);

		let response = await this.TalentAppealRequest.post().catch(e => { console.warn('ERROR: TalentAppealRequest', e); return false })

		if (!response) {
            this.TalentAppealRequest.didHandleError = await this.TalentAppealRequest.handleError();
            if (this.TalentAppealRequest.didHandleError
                && this.TalentAppealRequest.shouldRerun) {
                return this.talentUpdate(params);
            }

			return false;
		}

		console.log(response, 'response TalentAppealRequest')
		return true;

		// return this.update(User.make({ ...response.success.data.auth }))
	}

	/**
     * [Note]
     *
     * @param {Object} params
     * @returns {Promise}
     */
	static async deactivateAccount(params = {})
	{
		this.DeactivateAccountRequest = TB.request(
			TB.api_endpoint('/api/sessions/deactivate'), params
		);

		let response = await this.DeactivateAccountRequest.post().catch(
			console.warn
		);

		if (!response) {
            this.DeactivateAccountRequest.didHandleError = await this.DeactivateAccountRequest.handleError();
            if (this.DeactivateAccountRequest.didHandleError
                && this.DeactivateAccountRequest.shouldRerun) {
                return this.deactivateAccount(params);
            }

			return false;
		}

		await this.logout();
        return TB.sysAlert('talent.deactivate_account_confirmed');
    }

	/**
     * [Note]
     *
     * @param {Object} params
     * @returns {Promise}
     */
	static async reactivateAccount()
	{
		this.ReactivateAccountRequest = TB.request(
			TB.api_endpoint('/api/sessions/reactivate')
		);

		let response = await this.ReactivateAccountRequest.post().catch(
			console.warn
		);

		if (!response) {
            this.ReactivateAccountRequest.didHandleError = await this.ReactivateAccountRequest.handleError();
            if (this.ReactivateAccountRequest.didHandleError
                && this.ReactivateAccountRequest.shouldRerun) {
                return this.reactivateAccount();
            }

			return false;
		}

		this.User = { ...response.success.data.auth };
		this.DidExpire = false;

        TB.sysAlert('talent.reactivate_account_confirmed');
		return this.user();
	}


    /**
     * [Note]
     *
     * @returns {Object}
     */
	static authSettingsConfig(auth)
	{

        if (!auth) return [];
        if (auth.isClient()) {

			let groupPermission = []

			if (auth.client_project_groups?.length) {
				groupPermission = [
					{
						label: 'Group',
						config: [
							{
								label: "Permissions",
								config: TB.map(ProjectGroup.GroupedPermissions, (perms, permGroupKey) => {
									return {
										label: permGroupKey,
										config: TB.map(perms, (perm, key) => {
											let currentValue = TB.first(auth.client_project_groups)[perm];
											return {
												label: key,
												value: () => TB.first(auth.client_project_groups)[perm],
												setAuth: () => {

													return TB.reduce(auth.client_project_groups, (result, group, groupIndex) => {
														result[`client_project_groups[${groupIndex}].${perm}`] = !currentValue;
														return result;
													}, {})

													// return {
													//     [`client_project_groups[${groupIndex}].${perm}`]: !group[perm],
													// }
												},
												toggle: true
											}
										})
									}
								})
							}
						]
					}

				]
			}
            // let permissions =
            // console.log('authSettingsConfig', permissions)
            return [
                {
                    label: "Client Super Admin",
                    value: () => auth.isClientSuperAdmin() && !auth.isClientConsultant(),
                    setAuth: () => {
                        return {
                            'type': laravel.user_types.client,
                            'role_name': Client.Roles.SuperAdmin,
                            'is_client_super_admin': true,
                            'is_client_admin': false,
                            'client_organization.role': Client.Roles.SuperAdmin
                        }
                    }
                },
                {
                    label: "Client Admin",
                    value: () => auth.isClientAdmin() && !auth.isClientSuperAdmin(),
                    setAuth: () => {
                        return {
                            'type': laravel.user_types.client,
                            'role_name': Client.Roles.Admin,
                            'is_client_admin': true,
                            'is_client_super_admin': false,
                            'client_organization.role': Client.Roles.Admin
                        }
                    }
                },
                {
                    label: "Client Base User",
                    value: () => auth.isClientBaseUser(),
                    setAuth: () => {
                        return {
                            'type': laravel.user_types.client,
                            'role_name': Client.Roles.BaseUser,
                            'is_client_admin': false,
                            'is_client_super_admin': false,
                            'client_organization.role': Client.Roles.BaseUser
                        }
                    }
                },
                {
                    label: "Client Consultant",
                    value: () => auth.isClientConsultant(),
                    setAuth: () => {
                        return {
                            'type': laravel.user_types.client_consultant,
                            'role_name': Client.Roles.SuperAdmin,
                            'is_client_super_admin': true,
                            'client_organization.role': Client.Roles.SuperAdmin
                        }
                    }
                },
				...groupPermission
            ]
        }


		return [

        ];
	}






}

if (!TB.ScopedAuth) {
	TB.ScopedAuth = Auth;
}

export default Auth;